/**
 * Ingesta unificada: file input, drag & drop, PWA share_target e intents Capacitor.
 * Canaliza hacia los handlers de App.js (handleOpenLocalFs / handleOpenFolderDo).
 */

export const ImportSource = {
  FILE_INPUT: 'file_input',
  DRAG_DROP: 'drag_drop',
  SHARE_TARGET: 'share_target',
  CAPACITOR_INTENT: 'capacitor_intent',
}

export const PENDING_IMPORT_DB = 'udv-pending-imports'
export const PENDING_IMPORT_STORE = 'imports'

const DCM_EXT = /\.(dcm|dicom)$/i
const ZIP_EXT = /\.zip$/i

let dragTeardown = null
let capacitorListenerHandle = null

function isZipFile(file) {
  const name = (file.name || '').toLowerCase()
  return (
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed' ||
    ZIP_EXT.test(name)
  )
}

function isDicomFile(file) {
  const name = (file.name || '').toLowerCase()
  return (
    file.type === 'application/dicom' ||
    DCM_EXT.test(name) ||
    (file.type === 'application/octet-stream' && DCM_EXT.test(name))
  )
}

export function isAcceptedIncomingFile(file) {
  if (!file || !file.name) return false
  return isDicomFile(file) || isZipFile(file)
}

export function normalizeIncomingFiles(raw) {
  const arr = Array.from(raw || [])
  if (arr.length === 0) return []
  const isFolder = arr.some((f) => f.webkitRelativePath)
  if (isFolder) return arr
  return arr.filter(isAcceptedIncomingFile)
}

export function classifyIncomingFiles(files) {
  if (!files || files.length === 0) return null
  if (files.some((f) => f.webkitRelativePath)) return 'folder'
  if (files.length === 1 && isZipFile(files[0])) return 'zip'
  if (files.length === 1) return 'single'
  return 'multiple'
}

export function dispatchImport(files, handlers, meta = {}) {
  const normalized = normalizeIncomingFiles(files)
  const kind = classifyIncomingFiles(normalized)
  if (!kind) {
    console.warn('[importService] Sin archivos válidos', meta)
    return false
  }
  switch (kind) {
    case 'folder':
      handlers.onFolder(normalized)
      break
    case 'zip':
      handlers.onZip(normalized[0])
      break
    case 'single':
      handlers.onSingle(normalized[0])
      break
    case 'multiple':
      handlers.onMultiple(normalized)
      break
    default:
      return false
  }
  return true
}

function openPendingImportDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(PENDING_IMPORT_DB, 1)
    req.onerror = () => reject(req.error)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(PENDING_IMPORT_STORE)) {
        db.createObjectStore(PENDING_IMPORT_STORE)
      }
    }
    req.onsuccess = () => resolve(req.result)
  })
}

async function readPendingImport(importId) {
  const db = await openPendingImportDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_IMPORT_STORE, 'readonly')
    const store = tx.objectStore(PENDING_IMPORT_STORE)
    const req = store.get(importId)
    req.onerror = () => reject(req.error)
    req.onsuccess = () => resolve(req.result || null)
    tx.oncomplete = () => db.close()
  })
}

async function deletePendingImport(importId) {
  const db = await openPendingImportDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_IMPORT_STORE, 'readwrite')
    const store = tx.objectStore(PENDING_IMPORT_STORE)
    const req = store.delete(importId)
    req.onerror = () => reject(req.error)
    req.onsuccess = () => resolve()
    tx.oncomplete = () => db.close()
  })
}

function recordsToFiles(records) {
  if (!Array.isArray(records)) return []
  return records.map(
    (r) => new File([r.buffer], r.name, { type: r.type || 'application/octet-stream' })
  )
}

function stripImportIdFromUrl() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('importId')) return
  url.searchParams.delete('importId')
  const next =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
    url.hash
  window.history.replaceState({}, '', next)
}

export async function consumePendingShareImport(handlers) {
  const params = new URLSearchParams(window.location.search)
  const importId = params.get('importId')
  if (!importId) return false

  try {
    const payload = await readPendingImport(importId)
    await deletePendingImport(importId)
    stripImportIdFromUrl()

    if (!payload || !payload.files || payload.files.length === 0) {
      console.warn('[importService] importId sin archivos:', importId)
      return false
    }

    const files = recordsToFiles(payload.files)
    return dispatchImport(files, handlers, { source: ImportSource.SHARE_TARGET })
  } catch (err) {
    console.error('[importService] Error al consumir share import:', err)
    stripImportIdFromUrl()
    return false
  }
}

function preventDragDefaults(event) {
  event.preventDefault()
  event.stopPropagation()
}

export function setupDragDropListeners(handlers) {
  if (typeof document === 'undefined') return () => {}

  const onDragOver = (e) => preventDragDefaults(e)
  const onDrop = (e) => {
    preventDragDefaults(e)
    const dt = e.dataTransfer
    if (!dt || !dt.files || dt.files.length === 0) return
    dispatchImport(dt.files, handlers, { source: ImportSource.DRAG_DROP })
  }

  document.addEventListener('dragover', onDragOver)
  document.addEventListener('drop', onDrop)

  return () => {
    document.removeEventListener('dragover', onDragOver)
    document.removeEventListener('drop', onDrop)
  }
}

async function bootstrapCapacitorIntentListener() {
  try {
    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) return

    const { App } = await import('@capacitor/app')
    capacitorListenerHandle = await App.addListener('appUrlOpen', (event) => {
      console.log('[importService] Capacitor appUrlOpen:', event.url)
    })

    const launch = await App.getLaunchUrl()
    if (launch && launch.url) {
      console.log('[importService] Capacitor launch URL:', launch.url)
    }
  } catch (err) {
    console.warn('[importService] Capacitor listener no disponible:', err)
  }
}

export function bootstrapImportIngress(handlers) {
  consumePendingShareImport(handlers)
  dragTeardown = setupDragDropListeners(handlers)
  bootstrapCapacitorIntentListener()
}

export function teardownImportIngress() {
  if (typeof dragTeardown === 'function') {
    dragTeardown()
    dragTeardown = null
  }
  if (capacitorListenerHandle && typeof capacitorListenerHandle.remove === 'function') {
    capacitorListenerHandle.remove()
    capacitorListenerHandle = null
  }
}
